import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { Observable, from, throwError, lastValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthSessionService } from '../services/auth-session.service';
import { DebugLoggerService } from '../services/debug-logger.service';

const REAUTH_RETRY_DONE = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthExpiredInterceptor implements HttpInterceptor {
  private static readonly LOG_PREFIX = '[AmSuite Auth]';

  private _authSessionService = inject(AuthSessionService);
  private _debugLogger = inject(DebugLoggerService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.isAuthEndpoint(req.url) && req.context.get(REAUTH_RETRY_DONE)) {
          return from(this.handleRetryUnauthorized(req));
        }
        if (error.status === 401 && !this.isAuthEndpoint(req.url)) {
          return from(this.handleUnauthorized(req, next));
        }
        return throwError(() => error);
      })
    );
  }

  private isAuthEndpoint(url: string): boolean {
    return url.includes('b2clogin.com')
      || url.includes('/oauth2/')
      || url.includes('/token');
  }

  private async handleUnauthorized(req: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<any>> {
    console.log(`${AuthExpiredInterceptor.LOG_PREFIX} 401 on protected API — attempting recovery`, {
      url: req.url,
    });
    this._debugLogger.logAction('AuthExpiredInterceptor', '401 received, attempting silent refresh', {
      url: req.url
    });

    const token = await this._authSessionService.attemptSilentRefresh();

    if (token) {
      console.log(`${AuthExpiredInterceptor.LOG_PREFIX} silent refresh OK — retrying request`, {
        url: req.url,
      });
      this._debugLogger.logAction('AuthExpiredInterceptor', 'Silent refresh succeeded, retrying request', {
        url: req.url
      });
      const authedReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`),
        context: req.context.set(REAUTH_RETRY_DONE, true)
      });
      try {
        return await lastValueFrom(next.handle(authedReq));
      } catch (retryError: unknown) {
        const retryHttpError = retryError as HttpErrorResponse;
        if (retryHttpError?.status === 401) {
          return this.handleRetryUnauthorized(req);
        }
        throw retryError;
      }
    }

    console.warn(`${AuthExpiredInterceptor.LOG_PREFIX} silent refresh failed — starting reauthenticate`, {
      url: req.url,
    });
    await this._authSessionService.reauthenticate(`401 response - silent refresh failed (${req.url})`);
    throw new HttpErrorResponse({ status: 401, statusText: 'Session expired' });
  }

  private async handleRetryUnauthorized(req: HttpRequest<any>): Promise<never> {
    console.warn(`${AuthExpiredInterceptor.LOG_PREFIX} retry failed with 401 — starting reauthenticate`, {
      url: req.url,
    });
    this._debugLogger.logAction('AuthExpiredInterceptor', 'Retry returned 401, starting reauthenticate', {
      url: req.url
    });
    await this._authSessionService.reauthenticate(`401 response - retry failed after silent refresh (${req.url})`);
    throw new HttpErrorResponse({ status: 401, statusText: 'Session expired' });
  }
}
